import { useState } from 'react';
import { ArrowLeft, Pencil, Plus, Trash2 } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import { Badge, Button, Callout, Card, CardContent, CardDescription, CardHeader, CardTitle, ConfirmDialog, Spinner } from '../../components/ui';
import { cn } from '../../lib/utils';
import {
  EmailSeries,
  SeriesStatus,
  SeriesStep,
  SeriesVariable,
  useDeleteSeriesStep,
  useEmailSeries,
  useSeriesCandidates,
  useUpdateEmailSeries,
} from '../../queries/emailSeries';
import { EmailSeriesEnrolments } from './EmailSeriesEnrolments';
import { EmailSeriesSettings } from './EmailSeriesSettings';
import { EmailSeriesStepDialog } from './EmailSeriesStepDialog';
import { DRY_RUN_HELP, STATUS_LABELS, STATUS_VARIANTS, formatCalendarDate } from './emailSeriesFormat';

interface EmailSeriesDetailProps {
  seriesId: number;
  triggerDescription: string;
  variables: SeriesVariable[];
  canWrite: boolean;
  onBack: () => void;
}

export function EmailSeriesDetail({ seriesId, triggerDescription, variables, canWrite, onBack }: EmailSeriesDetailProps) {
  const { data, isLoading, error } = useEmailSeries(seriesId);
  // undefined = closed; null = adding a new step.
  const [editingStep, setEditingStep] = useState<SeriesStep | null | undefined>(undefined);

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={onBack}>
        <ArrowLeft />
        All series
      </Button>

      {isLoading && <Spinner />}
      {error && <Callout variant="error" title="Couldn't load this series">{extractErrorMessage(error)}</Callout>}

      {data && (
        <>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">{data.series.name}</h2>
              <Badge variant={STATUS_VARIANTS[data.series.status]}>{STATUS_LABELS[data.series.status]}</Badge>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">Starts when: {triggerDescription}</p>
          </div>

          <StatusCard series={data.series} problems={data.problems} warnings={data.warnings} canWrite={canWrite} />
          <EmailSeriesSettings series={data.series} discountOptions={data.discountOptions ?? []} canWrite={canWrite} />
          <StepsCard seriesId={seriesId} steps={data.steps} canWrite={canWrite} onEdit={setEditingStep} />
          <EmailSeriesEnrolments seriesId={seriesId} canWrite={canWrite} />

          {editingStep !== undefined && (
            <EmailSeriesStepDialog
              seriesId={seriesId}
              step={editingStep}
              variables={variables}
              canWrite={canWrite}
              onClose={() => setEditingStep(undefined)}
            />
          )}
        </>
      )}
    </div>
  );
}

const STATUS_ORDER: SeriesStatus[] = ['paused', 'dry_run', 'live'];

function StatusCard({ series, problems, warnings, canWrite }: { series: EmailSeries; problems: string[]; warnings: string[]; canWrite: boolean }) {
  const update = useUpdateEmailSeries();
  const [confirmingLive, setConfirmingLive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const blocked = problems.length > 0;

  const setStatus = (status: SeriesStatus) => {
    setError(null);
    update.mutate(
      { id: series.id, status },
      {
        onSuccess: () => setConfirmingLive(false),
        onError: (err) => {
          setConfirmingLive(false);
          setError(extractErrorMessage(err, 'Could not change the status.'));
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Status</CardTitle>
        <CardDescription>{DRY_RUN_HELP} Live sends real emails with real Stripe codes.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <div role="group" aria-label="Series status" className="inline-flex rounded-md border border-input p-0.5">
          {STATUS_ORDER.map((status) => {
            const current = series.status === status;
            const disabled = !canWrite || current || update.isPending || (status !== 'paused' && blocked);
            return (
              <button
                key={status}
                type="button"
                aria-pressed={current}
                disabled={disabled}
                onClick={() => (status === 'live' ? setConfirmingLive(true) : setStatus(status))}
                className={cn(
                  'rounded px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed',
                  current ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50 disabled:hover:bg-transparent',
                )}
              >
                {STATUS_LABELS[status]}
              </button>
            );
          })}
        </div>

        {blocked && (
          <Callout variant="error" title={series.status === 'paused' ? 'Fix these before a dry run or going live' : 'Fix these: this series cannot run as it is'}>
            <ul className="list-disc space-y-0.5 pl-4">
              {problems.map((p) => <li key={p}>{p}</li>)}
            </ul>
          </Callout>
        )}
        {warnings.length > 0 && (
          <Callout variant="warning" title="Worth checking">
            <ul className="list-disc space-y-0.5 pl-4">
              {warnings.map((w) => <li key={w}>{w}</li>)}
            </ul>
          </Callout>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}

        {confirmingLive && (
          <GoLiveDialog seriesId={series.id} busy={update.isPending} onConfirm={() => setStatus('live')} onCancel={() => setConfirmingLive(false)} />
        )}
      </CardContent>
    </Card>
  );
}

function GoLiveDialog({ seriesId, busy, onConfirm, onCancel }: { seriesId: number; busy: boolean; onConfirm: () => void; onCancel: () => void }) {
  const candidates = useSeriesCandidates(seriesId, true);
  const count = candidates.data?.count;

  return (
    <ConfirmDialog
      open
      title="Go live?"
      description="Real emails with real Stripe codes will be sent to these customers from the next pass. Pause the series to stop it."
      confirmLabel="Go live"
      busy={busy || candidates.isLoading}
      onConfirm={onConfirm}
      onOpenChange={(open) => !open && onCancel()}
    >
      {candidates.isLoading && <p className="text-muted-foreground">Checking who would be enrolled…</p>}
      {candidates.error && <p className="text-destructive">Couldn't check who would be enrolled: {extractErrorMessage(candidates.error)}</p>}
      {count !== undefined && (
        <div className="space-y-2">
          <p className="font-medium">
            {count === 0 ? 'Nobody would be enrolled on the next pass.' : `${count.toLocaleString('en-AU')} ${count === 1 ? 'person' : 'people'} would be enrolled on the next pass.`}
          </p>
          {candidates.data && candidates.data.sample.length > 0 && (
            <ul className="space-y-0.5 text-xs text-muted-foreground">
              {candidates.data.sample.slice(0, 5).map((c) => (
                <li key={c.userId}>
                  {c.name ?? 'No name'} {c.email ? `(${c.email})` : ''}, trigger {formatCalendarDate(c.triggerDate)}
                </li>
              ))}
              {count > 5 && <li>and {(count - 5).toLocaleString('en-AU')} more</li>}
            </ul>
          )}
        </div>
      )}
    </ConfirmDialog>
  );
}

function StepsCard({ seriesId, steps, canWrite, onEdit }: { seriesId: number; steps: SeriesStep[]; canWrite: boolean; onEdit: (step: SeriesStep | null) => void }) {
  const remove = useDeleteSeriesStep();
  const [deleting, setDeleting] = useState<SeriesStep | null>(null);
  const [error, setError] = useState<string | null>(null);
  const ordered = [...steps].sort((a, b) => a.stepOrder - b.stepOrder);

  const confirmDelete = () => {
    if (!deleting) return;
    setError(null);
    remove.mutate(
      { seriesId, stepId: deleting.id },
      {
        onSuccess: () => setDeleting(null),
        onError: (err) => {
          setDeleting(null);
          setError(extractErrorMessage(err, 'Could not delete the step.'));
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <div className="space-y-1">
          <CardTitle>Steps</CardTitle>
          <CardDescription>Day N is the number of days after the trigger the email goes out.</CardDescription>
        </div>
        {canWrite && (
          <Button size="sm" variant="outline" onClick={() => onEdit(null)}>
            <Plus />
            Add step
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-2 pt-0">
        {error && <Callout variant="error">{error}</Callout>}
        {ordered.length === 0 && <p className="text-sm text-muted-foreground">No steps yet.</p>}
        {ordered.map((step) => (
          <div key={step.id} data-testid={`step-${step.id}`} className="flex flex-wrap items-center gap-3 rounded-md border border-border px-3 py-2">
            <span className="w-14 shrink-0 text-sm font-semibold">Day {step.sendAfterDays}</span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{step.name}</p>
              <p className="truncate text-xs text-muted-foreground">{step.subject}</p>
            </div>
            <Badge variant={step.isActive ? 'success' : 'outline'}>{step.isActive ? 'On' : 'Off'}</Badge>
            <span className="text-xs text-muted-foreground">
              {step.stats.sent} sent · {step.stats.skipped} skipped · {step.stats.failed} failed
              {step.stats.dryRun > 0 ? ` · ${step.stats.dryRun} dry run` : ''}
            </span>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => onEdit(step)} aria-label={`${canWrite ? 'Edit' : 'View'} ${step.name}`}>
                <Pencil />
              </Button>
              {canWrite && (
                <Button size="sm" variant="ghost" onClick={() => setDeleting(step)} aria-label={`Delete ${step.name}`}>
                  <Trash2 />
                </Button>
              )}
            </div>
          </div>
        ))}
      </CardContent>
      <ConfirmDialog
        open={deleting !== null}
        title="Delete this step?"
        description={`"${deleting?.name ?? ''}" will be removed from the series. This can't be undone.`}
        confirmLabel="Delete step"
        destructive
        busy={remove.isPending}
        onConfirm={confirmDelete}
        onOpenChange={(open) => !open && setDeleting(null)}
      />
    </Card>
  );
}
